import Vue from "vue";
import { getField, updateField } from "vuex-map-fields";
import axios from "@/plugins/axios";
import { ANSWER_TYPES, normalizeAnswerType, requiredFieldErrors } from "@/config/answerTypes";
import { WIDGET_TYPE_SELECT_ITEMS } from "@/config/cwbWidgetTypes";
import { isShapeMockEnabled, mockShapeRequest } from "@/services/mappingWorkspaceShapeMock";

/**
 * SHAPE-aware request helper for the Readiness-backed Mapping Workspace reads
 * (application-workspace, application questions, SHAPE refresh).
 * - Mock flag on  -> isolated SHAPE mock (VUE_APP_SHAPE_MOCK).
 * - Flag off      -> live axios only. There is NO automatic fallback to the
 *   mock when a live request errors (errors propagate to the interceptor).
 */
async function shapeRequest(method, path, body) {
  if (isShapeMockEnabled()) {
    return mockShapeRequest(method, path, body);
  }
  return axios({ method, url: path, data: body });
}

/**
 * Isolated SHAPE backend-contract toggles — flip ON only when the backend
 * confirms the corresponding contract. Kept here (store layer) so components
 * never change when a contract is confirmed.
 *
 * Part 2B is SHAPE push -> UWB -> Readiness. By default the FE "refresh" just
 * re-pulls the existing Readiness GET workspace/questions; it does NOT assume a
 * dedicated POST refresh endpoint exists.
 */
const SHAPE_REFRESH_ENDPOINT_ENABLED = false;

/**
 * The answer type to show for a stored value.
 *
 * Legacy spellings are normalized so the list and the editor agree — the list
 * used to print the raw string while the editor's dropdown rendered blank for
 * it. An unrecognized value is passed through untouched rather than replaced
 * with a default, so it stays visible and save can reject it.
 */
function displayAnswerType(...candidates) {
  const raw = candidates.find((value) => typeof value === "string" && value.trim());
  if (!raw) return "";
  return normalizeAnswerType(raw) || raw;
}
// Send a durable ingested-question identifier (application_question_id /
// element_key) on mapping saves, in addition to raw_question_text. OFF until
// the backend confirms it accepts these fields.
const SHAPE_SEND_DURABLE_QUESTION_ID = true;

/**
 * Surface a business failure (success:false) as an error toast, for BOTH mock
 * and live responses. A live API may return HTTP 200 with success:false, which
 * the Axios response interceptor may not treat as an error, so always surface
 * it here using the existing showMessage pattern.
 */
function surfaceShapeError(dispatch, data, fallbackMessage) {
  dispatch(
    "showMessage",
    {
      message: data?.message || fallbackMessage,
      messageType: "error",
      messageTitle: "Error",
    },
    { root: true }
  );
}

function pickBestScannedApplication(apps = []) {
  return [...apps]
    .filter((app) => app.has_scanned_data !== false && (app.question_count || 0) > 0)
    .sort((a, b) => (b.question_count || 0) - (a.question_count || 0))[0];
}

/**
 * Normalize a carrier/SHAPE question from the API into the shape the mapping
 * views consume. Preserves every existing field and guarantees the SHAPE
 * additions (element_key + a canonical suggestion descriptor) are present in a
 * single casing, tolerating snake_case or camelCase from the backend.
 */
function normalizeCarrierQuestion(q = {}) {
  const suggestionId = q.suggestionCanonicalQuestionId ?? q.suggestion_canonical_question_id ?? null;
  const suggestionKey = q.suggestionCanonicalKey ?? q.suggestion_canonical_key ?? null;
  const suggestionLabel = q.suggestionCanonicalLabel ?? q.suggestion_canonical_label ?? null;
  return {
    ...q,
    elementKey: q.elementKey ?? q.element_key ?? null,
    source: q.source ?? null,
    mappingStatus: q.mappingStatus ?? q.mapping_status ?? null,
    suggestionCanonicalQuestionId: suggestionId,
    suggestionCanonicalKey: suggestionKey,
    suggestionCanonicalLabel: suggestionLabel,
  };
}

/** Normalize a blank-template question; durable id is question_key → element_key. */
export function normalizeTemplateQuestion(q = {}) {
  const text = q.question_text || q.label || q.text || q.question || "";
  const questionKey = q.question_key || q.questionKey || null;
  const elementKey = q.element_key || q.elementKey || questionKey || null;
  return normalizeCarrierQuestion({
    ...q,
    text,
    question: text,
    category: q.category || "Template",
    answerType: displayAnswerType(q.answer_type, q.answerType),
    confidence: q.confidence ?? null,
    position: q.position ?? 0,
    questionKey,
    element_key: elementKey,
    elementKey,
    source: q.source || "template",
    mappedTo: q.mappedTo ?? q.mapped_to ?? null,
  });
}

function normalizeQuestionStatus(status) {
  const statusMap = {
    published: "Published",
    draft: "Draft",
    deprecated: "Deprecated",
  };
  return statusMap[(status || "published").toLowerCase()] || "Published";
}

function mapPlacements(placements) {
  return (placements || []).map((p) => ({
    surface: p.surface || "",
    screen: p.screen || "",
    sortOrder: Number(p.sort_order ?? p.sortOrder ?? 0),
    visible: p.visible !== false && p.visible !== 0,
  }));
}

const state = {
  questions: [],
  questionsLoading: false,
  addQuestionDialog: false,
  editQuestionDialog: false,
  selectedQuestion: null,
  nextQuestionId: 11,
  versionHistoryDialog: false,
  selectedCanonicalQuestionIdForVersionHistory: null,
  selectedMappingIdForVersionHistory: null,
  selectedProductIdForVersionHistory: null,
  versionHistoryLabel: "",
  versionHistory: {},
  // Authoring seed only. The `categories` getter widens this with the
  // categories questions actually carry: the library holds 54 distinct
  // categories seeded from the catalog and screen titles, and a select limited
  // to these seven rendered blank for nearly every published question.
  categories: [
    "Access Control",
    "Training",
    "Incident Response",
    "Company Info",
    "Data Management",
    "Security Testing",
    "Network Security",
  ],
  answerTypes: ANSWER_TYPES,
  // Widget types live in the shared CWB config (src/config/cwbWidgetTypes.js)
  // so the authoring UI and the future generic renderer stay in agreement.
  widgetTypes: WIDGET_TYPE_SELECT_ITEMS,
  placementSurfaces: ["company_setup"],
  carriers: [],
  carriersLoading: false,
  productsByCarrier: {},
  uwbApplicationsByCarrier: {},
  assignmentsByProduct: {},
  carrierQuestionsByApp: {},
  activeApplicationByProduct: {},
  assignedApplicationsByProduct: {},
  carrierMappingsByQuestion: {},

  deleteDeprecateModal: {
    open: false,
    question: null,
  },
  showDeprecated: false,
};

const mutations = {
  updateField,

  SET_QUESTIONS(state, questions) {
    state.questions = questions;
  },

  addQuestion(state, question) {
    state.questions.push(question);
  },

  ADD_QUESTION(state, question) {
    state.questions.unshift(question);
    state.nextQuestionId++;
  },

  SET_QUESTIONS_LOADING(state, loading) {
    state.questionsLoading = loading;
  },

  SET_CARRIERS(state, carriers) {
    state.carriers = carriers;
  },

  SET_CARRIERS_LOADING(state, loading) {
    state.carriersLoading = loading;
  },

  SET_PRODUCTS_FOR_CARRIER(state, { carrierId, products }) {
    state.productsByCarrier = {
      ...state.productsByCarrier,
      [carrierId]: products,
    };
  },

  SET_UWB_APPLICATIONS_FOR_CARRIER(state, { carrierId, applications }) {
    state.uwbApplicationsByCarrier = {
      ...state.uwbApplicationsByCarrier,
      [carrierId]: applications,
    };
  },

  SET_ASSIGNMENTS_FOR_PRODUCT(state, { productId, assignments }) {
    state.assignmentsByProduct = {
      ...state.assignmentsByProduct,
      [productId]: assignments,
    };
  },

  SET_CARRIER_QUESTIONS_FOR_APP(state, { productId, applicationId, questions }) {
    const key = `${productId}:${applicationId}`;
    state.carrierQuestionsByApp = {
      ...state.carrierQuestionsByApp,
      [key]: questions,
    };
  },

  SET_ACTIVE_APPLICATION_FOR_PRODUCT(state, { productId, applicationId }) {
    state.activeApplicationByProduct = {
      ...state.activeApplicationByProduct,
      [productId]: applicationId,
    };
  },

  SET_ASSIGNED_APPLICATIONS_FOR_PRODUCT(state, { productId, applications }) {
    state.assignedApplicationsByProduct = {
      ...state.assignedApplicationsByProduct,
      [productId]: applications,
    };
  },

  SET_ADD_QUESTION_DIALOG(state, value) {
    state.addQuestionDialog = value;
  },

  SET_EDIT_QUESTION_DIALOG(state, value) {
    state.editQuestionDialog = value;
  },

  SET_SELECTED_QUESTION(state, question) {
    state.selectedQuestion = question;
  },

  UPDATE_QUESTION(state, updatedQuestion) {
    const index = state.questions.findIndex((q) => q.id === updatedQuestion.id);
    if (index !== -1) {
      state.questions.splice(index, 1, updatedQuestion);
    }
  },

  SET_DELETE_DEPRECATE_MODAL(state, { open, question }) {
    state.deleteDeprecateModal = { open, question: question || null };
  },

  REMOVE_QUESTION(state, questionId) {
    state.questions = state.questions.filter((q) => q.id !== questionId);
  },

  DEPRECATE_QUESTION(state, questionId) {
    const index = state.questions.findIndex((q) => q.id === questionId);
    if (index !== -1) {
      // Only the status changes here. Deprecation is staged in the draft, so
      // the question's stored changed_on does not move and must not be
      // restamped with today.
      state.questions.splice(index, 1, {
        ...state.questions[index],
        status: "Deprecated",
      });
    }
  },

  SET_SHOW_DEPRECATED(state, value) {
    state.showDeprecated = value;
  },

  OPEN_VERSION_HISTORY_DIALOG(state, { canonicalQuestionId = null, mappingId = null, productId = null, label = "" }) {
    state.versionHistoryDialog = true;
    state.selectedCanonicalQuestionIdForVersionHistory = canonicalQuestionId;
    state.selectedMappingIdForVersionHistory = mappingId;
    state.selectedProductIdForVersionHistory = productId;
    state.versionHistoryLabel = label;
  },

  CLOSE_VERSION_HISTORY_DIALOG(state) {
    state.versionHistoryDialog = false;
    state.selectedCanonicalQuestionIdForVersionHistory = null;
    state.selectedMappingIdForVersionHistory = null;
    state.selectedProductIdForVersionHistory = null;
    state.versionHistoryLabel = "";
  },

  SET_VERSION_HISTORY(state, { cacheKey, questionId, versions }) {
    const key = cacheKey || questionId;
    if (!key) return;
    Vue.set(state.versionHistory, key, versions);
  },

  SET_CARRIER_MAPPINGS_FOR_QUESTION(state, { questionId, mappings }) {
    Vue.set(state.carrierMappingsByQuestion, questionId, mappings || []);
  },
};

const actions = {
  async fetchCarriers({ commit }) {
    commit("SET_CARRIERS_LOADING", true);
    try {
      const { data } = await axios.get("/admin/mapping-workspace/carriers");
      if (data.success) {
        commit("SET_CARRIERS", data.carriers || []);
      }
    } finally {
      commit("SET_CARRIERS_LOADING", false);
    }
  },

  async fetchCarrierProducts({ commit }, carrierId) {
    const { data } = await axios.get(`/admin/mapping-workspace/carriers/${carrierId}/products`);
    if (data.success) {
      commit("SET_PRODUCTS_FOR_CARRIER", { carrierId, products: data.products || [] });
    }
    return data.products || [];
  },

  async createCarrierProduct({ dispatch }, { carrierId, name, insuranceLine, description }) {
    const { data } = await axios.post(
      `/admin/mapping-workspace/carriers/${carrierId}/products`,
      {
        name,
        insurance_line: insuranceLine,
        description,
      },
      { withoutLoading: true }
    );
    if (data.success) {
      await dispatch("fetchCarrierProducts", carrierId);
      await dispatch("fetchCarriers");
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    }
    return data;
  },

  async uploadCarrierApplicationPdf(_, { carrierId, file, onProgress }) {
    const formData = new FormData();
    formData.append("file", file);
    const fileSize = Math.max(file?.size || 0, 0);

    const { data } = await axios.post(
      `/admin/mapping-workspace/carriers/${carrierId}/application-templates/uploads`,
      formData,
      {
        withoutLoading: true,
        onUploadProgress: (e) => {
          if (!onProgress) return;
          const total = e?.lengthComputable && e.total > 0 ? e.total : fileSize;
          if (!total) return;
          onProgress(Math.max(0, Math.min(Math.round((e.loaded / total) * 80), 80)));
        },
        transformRequest: [
          (body, headers) => {
            delete headers["Content-Type"];
            delete headers["content-type"];
            return body;
          },
        ],
      }
    );

    if (data?.success && data.storage_ref && onProgress) onProgress(100);
    return data;
  },

  async createCarrierProductWithApplication(
    { dispatch },
    { carrierId, name, insuranceLine, description, storageRef, filename }
  ) {
    const { data } = await axios.post(
      `/admin/mapping-workspace/carriers/${carrierId}/products-with-application`,
      {
        name,
        insurance_line: insuranceLine,
        description,
        storage_ref: storageRef,
        filename,
      },
      { withoutLoading: true }
    );
    if (data.success) {
      await dispatch("fetchCarrierProducts", carrierId);
      await dispatch("fetchCarriers");
      const status = String(data.template?.status || "").toLowerCase();
      if (status === "processed") {
        dispatch(
          "showMessage",
          { ...data, messageType: "success", messageTitle: "Success" },
          { root: true }
        );
      }
    }
    return data;
  },

  async retryProductApplicationTemplate({ dispatch }, { carrierId, productId }) {
    let data;
    try {
      ({ data } = await axios.post(
        `/admin/mapping-workspace/carriers/${carrierId}/products/${productId}/application-template/retry`,
        {},
        { withoutLoading: true }
      ));
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Could not retry extraction. Try again.",
      };
    }
    if (data?.success) {
      await dispatch("fetchCarrierProducts", carrierId);
    }
    return data;
  },

  async fetchProductApplicationTemplate(_, { carrierId, productId }) {
    try {
      const { data } = await axios.get(
        `/admin/mapping-workspace/carriers/${carrierId}/products/${productId}/application-template`,
        { withoutLoading: true }
      );
      if (data?.success !== true) {
        return { success: false, template: null, questions: [], message: data?.message };
      }
      return {
        success: true,
        template: data.template || null,
        questions: (data.questions || []).map(normalizeTemplateQuestion),
      };
    } catch (error) {
      return {
        success: false,
        template: null,
        questions: [],
        message: error?.response?.data?.message || "Could not load template questions.",
      };
    }
  },

  async attachProductApplicationTemplate({ dispatch }, { carrierId, productId, storageRef, filename }) {
    let data;
    try {
      ({ data } = await axios.post(
        `/admin/mapping-workspace/carriers/${carrierId}/products/${productId}/application-template`,
        { storage_ref: storageRef, filename },
        { withoutLoading: true }
      ));
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Could not attach the application. Try again.",
      };
    }
    if (data?.success) {
      await dispatch("fetchCarrierProducts", carrierId);
    }
    return data;
  },

  async updateCarrierProduct({ dispatch }, { carrierId, productId, name, insuranceLine, description }) {
    const { data } = await axios.put(
      `/admin/mapping-workspace/carriers/${carrierId}/products/${productId}`,
      { name, insurance_line: insuranceLine, description },
      { withoutLoading: true }
    );
    if (data.success) {
      await dispatch("fetchCarrierProducts", carrierId);
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    }
    return data;
  },

  async deleteCarrierProduct({ dispatch }, { carrierId, productId }) {
    const { data } = await axios.delete(`/admin/mapping-workspace/carriers/${carrierId}/products/${productId}`, {
      withoutLoading: true,
    });
    if (data.success) {
      await dispatch("fetchCarrierProducts", carrierId);
      await dispatch("fetchCarriers");
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    }
    return data;
  },

  async archiveCarrierProduct({ dispatch }, { carrierId, productId }) {
    const { data } = await axios.post(
      `/admin/mapping-workspace/carriers/${carrierId}/products/${productId}/archive`,
      null,
      { withoutLoading: true }
    );
    if (data.success) {
      await dispatch("fetchCarrierProducts", carrierId);
      await dispatch("fetchCarriers");
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    }
    return data;
  },

  async unarchiveCarrierProduct({ dispatch }, { carrierId, productId }) {
    const { data } = await axios.post(
      `/admin/mapping-workspace/carriers/${carrierId}/products/${productId}/unarchive`,
      null,
      { withoutLoading: true }
    );
    if (data.success) {
      await dispatch("fetchCarrierProducts", carrierId);
      await dispatch("fetchCarriers");
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    }
    return data;
  },

  async fetchUwbApplications({ commit }, carrierId) {
    const { data } = await axios.get(`/admin/mapping-workspace/carriers/${carrierId}/applications`);
    if (data.success) {
      commit("SET_UWB_APPLICATIONS_FOR_CARRIER", {
        carrierId,
        applications: data.applications || [],
      });
    }
    return data.applications || [];
  },

  async fetchProductAssignments({ commit }, productId) {
    const { data } = await axios.get(`/admin/mapping-workspace/products/${productId}/assignments`);
    if (data.success) {
      commit("SET_ASSIGNMENTS_FOR_PRODUCT", {
        productId,
        assignments: data.assignments || [],
      });
    }
    return data.assignments || [];
  },

  async assignApplicationToProduct({ dispatch }, { productId, applicationId }) {
    const { data } = await axios.post(`/admin/mapping-workspace/products/${productId}/assignments`, {
      application_id: applicationId,
    });
    if (data.success) {
      await dispatch("fetchProductAssignments", productId);
    }
    return data;
  },

  async fetchProductApplicationWorkspace({ commit, dispatch }, productId) {
    const { data } = await shapeRequest("get", `/admin/mapping-workspace/products/${productId}/application-workspace`);
    if (!data.success) {
      surfaceShapeError(dispatch, data, "Failed to load carrier applications.");
      return { assigned: [], available: [] };
    }
    const assigned = data.assigned || [];
    // Cache assigned applications so the UI can list/label them (e.g. SHAPE).
    commit("SET_ASSIGNED_APPLICATIONS_FOR_PRODUCT", { productId, applications: assigned });
    return {
      assigned,
      available: data.available || [],
    };
  },

  async fetchApplicationQuestions({ commit, dispatch }, { productId, applicationId }) {
    const { data } = await shapeRequest(
      "get",
      `/admin/mapping-workspace/products/${productId}/applications/${applicationId}/questions`
    );
    if (data.success) {
      commit("SET_CARRIER_QUESTIONS_FOR_APP", {
        productId,
        applicationId,
        questions: (data.questions || []).map(normalizeCarrierQuestion),
      });
      commit("SET_ACTIVE_APPLICATION_FOR_PRODUCT", { productId, applicationId });
    } else {
      surfaceShapeError(dispatch, data, "Failed to load application questions.");
    }
    return data;
  },

  /**
   * Resolve which application's questions to show and load them.
   * Preference order (so a user's selection is preserved across navigation and
   * refresh, and only a genuine cold reload falls back to the best scan):
   *   1. an explicitly requested applicationId (if still assigned)
   *   2. the currently active application (if still assigned)
   *   3. the best assigned scanned application
   */
  async loadProductQuestions({ dispatch, getters }, { productId, applicationId = null }) {
    const workspace = await dispatch("fetchProductApplicationWorkspace", productId);
    const assigned = workspace.assigned || [];
    const isAssigned = (id) => !!id && assigned.some((a) => a.entity_id === id);
    const activeId = getters.getActiveApplicationId(productId);

    const resolvedApplicationId =
      (isAssigned(applicationId) && applicationId) ||
      (isAssigned(activeId) && activeId) ||
      pickBestScannedApplication(assigned)?.entity_id;

    if (!resolvedApplicationId) {
      return { success: false, questions: [], application: null };
    }

    return dispatch("fetchApplicationQuestions", {
      productId,
      applicationId: resolvedApplicationId,
    });
  },

  async saveQuestionMapping(
    _,
    { productId, question, canonicalQuestionId = null, confirmSuggestion = true, mappingStatus = "mapped" }
  ) {
    const payload = {
      raw_question_text: question.text || question.question,
      confirm_suggestion: confirmSuggestion,
      mapping_status: mappingStatus,
    };
    if (canonicalQuestionId) {
      payload.canonical_question_id = canonicalQuestionId;
    }
    // Persist durable id (SHAPE element_key or template question_key).
    if (SHAPE_SEND_DURABLE_QUESTION_ID) {
      if (question.id != null) {
        payload.application_question_id = question.id;
      }
      const elementKey =
        question.elementKey || question.element_key || question.questionKey || question.question_key;
      if (elementKey) {
        payload.element_key = elementKey;
      }
    }
    const { data } = await axios.post(`/admin/mapping-workspace/products/${productId}/mappings`, payload);
    return data;
  },

  async suggestCanonicalMapping(_, { productId, questionText, elementKey = null }) {
    const payload = { raw_question_text: questionText };
    if (elementKey) {
      payload.element_key = elementKey;
    }
    const { data } = await axios.post(`/admin/mapping-workspace/products/${productId}/mappings/suggest`, payload);
    return data;
  },

  /**
   * Re-pull Readiness workspace/questions for the product (optional POST behind flag).
   */
  async refreshCarrierApplications({ commit, dispatch }, { carrierId, productId }) {
    if (SHAPE_REFRESH_ENDPOINT_ENABLED) {
      const { data } = await shapeRequest(
        "post",
        `/admin/mapping-workspace/products/${productId}/applications/refresh`,
        { carrier_id: carrierId }
      );
      if (!data.success) {
        surfaceShapeError(dispatch, data, "Failed to refresh carrier applications.");
        return { success: false, message: data.message };
      }
      if (data.assigned) {
        commit("SET_ASSIGNED_APPLICATIONS_FOR_PRODUCT", { productId, applications: data.assigned });
      }
    }

    // Re-pull the latest Readiness data, keeping the active application.
    const result = await dispatch("loadProductQuestions", { productId });
    if (result.success === false) {
      // The underlying workspace/questions fetch already surfaced the business
      // error via surfaceShapeError; do not show the success toast, and do not
      // toast again here (avoids duplicate error messages).
      return { success: false, message: result.message };
    }
    dispatch(
      "showMessage",
      {
        message: "Latest carrier data loaded.",
        messageType: "success",
        messageTitle: "Refreshed",
      },
      { root: true }
    );
    return { success: true, ...result };
  },

  async previewMapping(_, { productId, canonicalQuestionId, sampleAnswer }) {
    const { data } = await axios.post(`/admin/mapping-workspace/products/${productId}/mappings/preview`, {
      canonical_question_id: canonicalQuestionId,
      sample_answer: sampleAnswer,
    });
    return data;
  },

  async fetchMappingVersionHistory({ commit }, mappingId) {
    const { data } = await axios.get(`/admin/mapping-workspace/mappings/${mappingId}/versions`);
    if (data.success) {
      commit("SET_VERSION_HISTORY", {
        cacheKey: mappingId,
        versions: {
          versions: data.versions || [],
          currentVersionNumber: data.currentVersionNumber || 0,
        },
      });
    }
    return data;
  },

  async fetchCanonicalVersionHistory({ commit }, canonicalQuestionId) {
    const { data } = await axios.get(`/admin/mapping-workspace/canonical-questions/${canonicalQuestionId}/versions`);
    if (data.success) {
      commit("SET_VERSION_HISTORY", {
        cacheKey: canonicalQuestionId,
        versions: {
          versions: data.versions || [],
          currentVersionNumber: data.currentVersionNumber || 0,
        },
      });
    }
    return data;
  },

  // The canonical-questions endpoint is the single source of truth: it already
  // excludes questions staged for removal and returns them again after a
  // Discard Draft, so the library renders its response as-is.
  async loadQuestions({ commit }) {
    commit("SET_QUESTIONS_LOADING", true);

    try {
      const { data } = await axios.get("/admin/mapping-workspace/canonical-questions");
      if (!data.success) {
        commit("SET_QUESTIONS", []);
        return;
      }
      const questions = (data.questions || []).map((q) => {
        let answerOptions = [];
        // Part 1 field is `answer_options`; fall back to legacy `options`.
        const rawOptions = q.answer_options ?? q.options;
        if (rawOptions) {
          try {
            const parsed = typeof rawOptions === "string" ? JSON.parse(rawOptions) : rawOptions;
            answerOptions = Array.isArray(parsed) ? parsed : [];
          } catch {
            answerOptions = [];
          }
        }
        return {
          id: q.id ?? q.entity_id,
          entity_id: q.entity_id ?? q.id,
          text: q.text,
          answerType: displayAnswerType(q.answerType, q.answer_type),
          answerOptions,
          helpText: q.help_text ?? q.helpText ?? "",
          displayLabel: q.display_label ?? q.displayLabel ?? "",
          category: q.category,
          dependency: q.dependency || null,
          widgetType: q.widget_type ?? null,
          placements: mapPlacements(q.placements),
          mappedCarrierCount: q.mappedCarrierCount ?? q.mapped_carrier_count ?? 0,
          mappedCarriers: q.mappedCarriers || q.mapped_carriers || [],
          // The row's own changed_on (or, for an unpublished draft addition,
          // when it was staged). Never today's date: every question sharing
          // one date was this field being stamped at load time.
          lastUpdated: q.last_updated ?? q.lastUpdated ?? null,
          versionNumber: q.version_number ?? q.versionNumber ?? 1,
          status: normalizeQuestionStatus(q.status),
          validationRules: q.validation_rules ?? "",
          constraints: q.constraints ?? "",
          notes: q.notes ?? "",
          canonicalKey: q.canonical_key,
        };
      });
      commit("SET_QUESTIONS", questions);
    } catch (error) {
      console.error("Failed to load canonical questions", error);
      commit("SET_QUESTIONS", []);
    }

    commit("SET_QUESTIONS_LOADING", false);
  },

  async fetchQuestionCarrierMappings({ commit }, questionId) {
    const { data } = await axios.get(`/admin/mapping-workspace/canonical-questions/${questionId}/carrier-mappings`);
    const mappings = data.success ? data.mappings || [] : [];
    commit("SET_CARRIER_MAPPINGS_FOR_QUESTION", { questionId, mappings });
    return mappings;
  },

  async createQuestion({ commit, dispatch }, questionData) {
    // Guarded here rather than only in the form so every caller — the add
    // modal, the editor and any direct dispatch — is held to the same rule.
    const fieldErrors = requiredFieldErrors(questionData);
    if (Object.keys(fieldErrors).length) {
      return { success: false, error: Object.values(fieldErrors).join(" "), fieldErrors };
    }

    const { data } = await axios.post("/admin/mapping-workspace/canonical-questions", {
      text: questionData.text,
      category: questionData.category,
      answer_type: questionData.answerType,
      answer_options: questionData.answerOptions,
      display_label: questionData.displayLabel,
      help_text: questionData.helpText,
      dependency: questionData.dependency,
      validation_rules: questionData.validationRules,
      constraints: questionData.constraints,
      widget_type: questionData.widgetType,
      placements: questionData.placements,
      notes: questionData.notes,
    });
    if (!data.success) {
      return { success: false, error: data.message, fieldErrors: data.errors || {} };
    }

    const newQuestion = {
      id: data.question.id ?? data.question.entity_id,
      entity_id: data.question.entity_id ?? data.question.id,
      text: data.question.text,
      answerType: displayAnswerType(data.question.answerType, data.question.answer_type),
      answerOptions: Array.isArray(questionData.answerOptions) ? questionData.answerOptions : [],
      helpText: data.question.help_text ?? questionData.helpText ?? "",
      displayLabel: data.question.display_label ?? questionData.displayLabel ?? "",
      category: data.question.category,
      dependency: questionData.dependency || null,
      widgetType: questionData.widgetType === undefined ? data.question.widget_type ?? null : questionData.widgetType,
      placements: mapPlacements(questionData.placements),
      mappedCarrierCount: 0,
      mappedCarriers: [],
      lastUpdated: new Date().toISOString().split("T")[0],
      status: "Draft",
      validationRules: data.question.validation_rules ?? questionData.validationRules ?? "",
      constraints: data.question.constraints ?? questionData.constraints ?? "",
      notes: data.question.notes ?? questionData.notes ?? "",
      canonicalKey: data.question.canonical_key,
    };
    commit("ADD_QUESTION", newQuestion);
    dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    return { success: true, question: newQuestion };
  },

  checkDuplicateQuestion({ state }, questionText) {
    const normalizedText = questionText.toLowerCase().trim();
    return state.questions.some((q) => q.text.toLowerCase().trim() === normalizedText);
  },

  openAddQuestionDialog({ commit }) {
    commit("SET_ADD_QUESTION_DIALOG", true);
  },

  closeAddQuestionDialog({ commit }) {
    commit("SET_ADD_QUESTION_DIALOG", false);
  },

  async addQuestion({ dispatch }, questionData) {
    const result = await dispatch("createQuestion", questionData);
    if (result.success) {
      dispatch("closeAddQuestionDialog");
    }
    return result;
  },

  openDeleteDeprecateModal({ commit }, question) {
    commit("SET_DELETE_DEPRECATE_MODAL", { open: true, question });
  },

  closeDeleteDeprecateModal({ commit }) {
    commit("SET_DELETE_DEPRECATE_MODAL", { open: false, question: null });
  },

  /**
   * Stage a canonical question removal. Delete and deprecate hit the same
   * endpoint (the backend always stages a "removed" draft change), so both
   * actions share this implementation.
   *
   * On failure nothing is committed and the modal stays open — the axios
   * response interceptor already surfaces the API message as an error toast,
   * so the UI never reports a removal the backend rejected.
   */
  async stageQuestionRemoval({ commit, dispatch }, questionId) {
    const { data } = await axios.delete(`/admin/mapping-workspace/canonical-questions/${questionId}`);
    if (!data.success) {
      return { success: false, error: data.message };
    }
    // Immediate feedback; the next loadQuestions takes the backend's word for it.
    // A discarded draft addition leaves nothing behind, so drop the row rather
    // than showing it as deprecated.
    commit(data.status === "Discarded" ? "REMOVE_QUESTION" : "DEPRECATE_QUESTION", questionId);
    dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    dispatch("closeDeleteDeprecateModal");
    return { success: true };
  },

  async deleteQuestion(context, questionId) {
    return actions.stageQuestionRemoval(context, questionId);
  },

  async deprecateQuestion(context, questionId) {
    return actions.stageQuestionRemoval(context, questionId);
  },

  setShowDeprecated({ commit }, value) {
    commit("SET_SHOW_DEPRECATED", value);
  },

  openEditQuestionDialog({ commit }, question) {
    commit("SET_SELECTED_QUESTION", question);
    commit("SET_EDIT_QUESTION_DIALOG", true);
  },

  closeEditQuestionDialog({ commit }) {
    commit("SET_EDIT_QUESTION_DIALOG", false);
    commit("SET_SELECTED_QUESTION", null);
  },

  async updateQuestion({ commit, dispatch, state }, questionData) {
    const existing = state.questions.find((q) => q.id === questionData.id);
    // Effective values: a partial update is judged on what the question will
    // hold once applied, not only on the fields this call happens to carry.
    const fieldErrors = requiredFieldErrors({
      answerType: questionData.answerType ?? existing?.answerType,
      category: questionData.category ?? existing?.category,
    });
    if (Object.keys(fieldErrors).length) {
      return { success: false, error: Object.values(fieldErrors).join(" "), fieldErrors };
    }

    const { data } = await axios.put(`/admin/mapping-workspace/canonical-questions/${questionData.id}`, {
      text: questionData.text,
      category: questionData.category,
      answer_type: questionData.answerType,
      answer_options: questionData.answerOptions,
      display_label: questionData.displayLabel,
      help_text: questionData.helpText,
      dependency: questionData.dependency,
      validation_rules: questionData.validationRules,
      constraints: questionData.constraints,
      notes: questionData.notes,
      widget_type: questionData.widgetType,
      placements: questionData.placements,
      status: questionData.status,
    });
    if (!data.success) {
      return { success: false, error: data.message, fieldErrors: data.errors || {} };
    }

    const updatedQuestion = {
      ...existing,
      ...data.question,
      // Retain both id and entity_id (fallback each way) so Master Layout
      // placements and parentCanonicalId always resolve the canonical entity id.
      id: data.question.id ?? data.question.entity_id ?? existing?.id,
      entity_id: data.question.entity_id ?? data.question.id ?? existing?.entity_id,
      answerType: displayAnswerType(data.question.answerType, data.question.answer_type, existing?.answerType),
      answerOptions:
        questionData.answerOptions === undefined ? existing?.answerOptions ?? [] : questionData.answerOptions,
      helpText: questionData.helpText ?? data.question.help_text ?? existing?.helpText ?? "",
      displayLabel: questionData.displayLabel ?? data.question.display_label ?? existing?.displayLabel ?? "",
      widgetType:
        questionData.widgetType === undefined
          ? data.question.widget_type ?? existing?.widgetType ?? null
          : questionData.widgetType,
      placements: mapPlacements(
        questionData.placements === undefined
          ? data.question.placements ?? existing?.placements
          : questionData.placements
      ),
      dependency:
        questionData.dependency === undefined
          ? data.question.dependency ?? existing?.dependency ?? null
          : questionData.dependency,
      validationRules:
        questionData.validationRules ?? data.question.validation_rules ?? existing?.validationRules ?? "",
      constraints: questionData.constraints ?? data.question.constraints ?? existing?.constraints ?? "",
      notes: questionData.notes ?? data.question.notes ?? existing?.notes ?? "",
      // Editing stages a draft change; canonical_question.changed_on is only
      // rewritten when the draft is published, so keep what the server last
      // reported rather than stamping today.
      lastUpdated: data.question.last_updated ?? existing?.lastUpdated ?? null,
      status: normalizeQuestionStatus(questionData.status ?? data.question.status ?? existing?.status),
    };
    commit("UPDATE_QUESTION", updatedQuestion);
    dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    return { success: true, question: updatedQuestion };
  },

  async openVersionHistory({ commit, dispatch }, payload) {
    const canonicalQuestionId = typeof payload === "string" ? payload : payload?.canonicalQuestionId || null;
    const mappingId = typeof payload === "object" ? payload?.mappingId || null : null;
    const productId = typeof payload === "object" ? payload?.productId || null : null;
    const label = typeof payload === "object" ? payload?.label || "" : "";

    commit("OPEN_VERSION_HISTORY_DIALOG", { canonicalQuestionId, mappingId, productId, label });

    if (mappingId) {
      await dispatch("fetchMappingVersionHistory", mappingId);
      return;
    }

    if (canonicalQuestionId) {
      await dispatch("fetchCanonicalVersionHistory", canonicalQuestionId);
      return;
    }

    commit("SET_VERSION_HISTORY", {
      cacheKey: "__empty__",
      versions: { versions: [], currentVersionNumber: 0 },
    });
  },

  closeVersionHistory({ commit }) {
    commit("CLOSE_VERSION_HISTORY_DIALOG");
  },

  async rollbackQuestion({ commit, dispatch, state }, { questionId, versionNumber, mappingId }) {
    const resolvedMappingId = mappingId || state.selectedMappingIdForVersionHistory;
    if (resolvedMappingId) {
      const { data } = await axios.post(
        `/admin/mapping-workspace/mappings/${resolvedMappingId}/versions/${versionNumber}/rollback`
      );
      if (!data.success) {
        return { success: false, error: data.message };
      }
      await dispatch("fetchMappingVersionHistory", resolvedMappingId);
      if (state.selectedProductIdForVersionHistory) {
        await dispatch("loadProductQuestions", {
          productId: state.selectedProductIdForVersionHistory,
        });
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return { success: true, mapping: data.mapping || null };
    }

    const resolvedQuestionId = questionId || state.selectedCanonicalQuestionIdForVersionHistory;
    if (resolvedQuestionId) {
      const { data } = await axios.post(
        `/admin/mapping-workspace/canonical-questions/${resolvedQuestionId}/versions/${versionNumber}/rollback`
      );
      if (!data.success) {
        return { success: false, error: data.message };
      }
      await dispatch("fetchCanonicalVersionHistory", resolvedQuestionId);
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return { success: true, staged: true };
    }

    dispatch(
      "showMessage",
      {
        messageType: "error",
        messageTitle: "Rollback",
        message: "No version history context available for rollback.",
      },
      { root: true }
    );
    return { success: false };
  },
};

const getters = {
  getField,

  questions: (state) => state.questions,
  questionsLoading: (state) => state.questionsLoading,
  addQuestionDialog: (state) => state.addQuestionDialog,
  editQuestionDialog: (state) => state.editQuestionDialog,
  selectedQuestion: (state) => state.selectedQuestion,
  /**
   * Selectable categories: the authoring seed plus every category the loaded
   * questions actually carry.
   *
   * Sourced from the questions rather than invented, so a stored category is
   * always representable in the editor instead of silently showing blank.
   */
  categories: (state) => {
    const stored = state.questions.map((q) => q.category).filter((c) => typeof c === "string" && c.trim());
    return [...new Set([...state.categories, ...stored])].sort((a, b) => a.localeCompare(b));
  },
  answerTypes: (state) => state.answerTypes,
  widgetTypes: (state) => state.widgetTypes,
  placementSurfaces: (state) => state.placementSurfaces,
  carriers: (state) => state.carriers,
  carriersLoading: (state) => state.carriersLoading,
  totalQuestionsCount: (state) => state.questions.length,

  pickBestScannedApplication: () => (apps) => pickBestScannedApplication(apps),

  publishedQuestionsCount: (state) => state.questions.filter((q) => q.status === "Published").length,
  draftQuestionsCount: (state) => state.questions.filter((q) => q.status === "Draft").length,

  deleteDeprecateModal: (state) => state.deleteDeprecateModal,
  showDeprecated: (state) => state.showDeprecated,

  getDependentChildren: (state) => (questionId) => {
    const parent = state.questions.find((q) => q.id === questionId);
    const parentKey = parent?.canonicalKey;
    return state.questions.filter((q) => {
      if (!q.dependency) return false;
      const parentRef = q.dependency.split(/[><=! ]/)[0].trim();
      return parentRef === questionId || (parentKey && parentRef === parentKey);
    });
  },

  canDeleteQuestion: (state, getters) => (question) => {
    if (!question) return false;
    return question.mappedCarrierCount === 0;
  },

  canDeprecateQuestion: (state, getters) => (question) => {
    if (!question) return false;
    if (question.status === "Deprecated") return false;
    const dependents = getters.getDependentChildren(question.id);
    return dependents.length === 0;
  },

  getModalAction: (state, getters) => (question) => {
    if (!question) return null;
    const dependents = getters.getDependentChildren(question.id);
    if (dependents.length > 0) return "blocked";
    if (question.mappedCarrierCount === 0) return "delete";
    return "deprecate";
  },

  getQuestionMappings: (state) => (questionId) => {
    return state.carrierMappingsByQuestion[questionId] || [];
  },

  getQuestionImpact: () => () => ({
    total: 0,
    highRisk: 0,
    mediumRisk: 0,
    lowRisk: 0,
  }),

  getQuestionCarriers: () => () => [],

  activeVersionHistory(state) {
    const cacheKey = state.selectedMappingIdForVersionHistory || state.selectedCanonicalQuestionIdForVersionHistory;
    if (!cacheKey) {
      return state.versionHistory.__empty__ || { versions: [], currentVersionNumber: 0 };
    }
    return state.versionHistory[cacheKey] || { versions: [], currentVersionNumber: 0 };
  },

  versionHistoryDialog: (state) => state.versionHistoryDialog,

  selectedCanonicalQuestionIdForVersionHistory: (state) => state.selectedCanonicalQuestionIdForVersionHistory,

  selectedMappingIdForVersionHistory: (state) => state.selectedMappingIdForVersionHistory,

  selectedProductIdForVersionHistory: (state) => state.selectedProductIdForVersionHistory,

  versionHistoryLabel: (state) => state.versionHistoryLabel,

  versionCount: (state) => (questionId) => {
    const history = state.versionHistory[questionId];
    return history ? history.versions.length : 0;
  },

  isVersionRollbackEligible: (state) => (version) => {
    // Can only rollback draft versions, not published
    return version.status === "Draft" || version.status === "Published";
  },

  getCarrierProducts: (state) => (carrierId) => {
    return state.productsByCarrier[carrierId] || [];
  },

  getUwbApplications: (state) => (carrierId) => {
    return state.uwbApplicationsByCarrier[carrierId] || [];
  },

  getProductAssignments: (state) => (productId) => {
    return state.assignmentsByProduct[productId] || [];
  },

  getActiveApplicationId: (state) => (productId) => {
    return state.activeApplicationByProduct[productId] || null;
  },

  getAssignedApplications: (state) => (productId) => {
    return state.assignedApplicationsByProduct[productId] || [];
  },

  getCarrierQuestions: (state) => (productId, applicationId) => {
    if (!productId || !applicationId) return [];
    return state.carrierQuestionsByApp[`${productId}:${applicationId}`] || [];
  },

  canonicalQuestionByRef: (state) => (ref) => {
    if (!ref) return null;
    return state.questions.find((q) => q.id === ref || q.canonicalKey === ref) || null;
  },

  canonicalKeyForRef: (state) => (ref) => {
    const question = state.questions.find((q) => q.id === ref || q.canonicalKey === ref);
    return question?.canonicalKey || "";
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
